import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ReservationSummaryAccomodationOfferRoomsDialogOfferChangeDialogComponent } from './reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog/reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog.component';
import { ReservationSummaryStoreService } from '@/reservation/reservation-summary/services/reservation-summary-store.service';
import { ReservationSummaryApiService } from '@/reservation/reservation-summary/services/reservation-summary-api.service';
import { OskarOfferUrlBuilder } from '@/common/oskar-offer-url-builder';
import {
	ChangeOfferFilters,
	ChangeOfferSelected,
	Offer,
	OfferFull,
	OfferService,
	Location,
	DateRange,
	ProductToServiceFilter,
} from '@/reservation/reservation.model';
import { LoaderService } from '@/common/loader/loader.service';
import { DateService } from '@/common/date.service';
import { MatPaginator } from '@angular/material/paginator';
import { DateAdapter } from '@angular/material/core';
import { Observable } from 'rxjs';
import { CancellationStatuses, Status } from '@/common/enums/Status';
import { OfferChangeTypes } from '@/common/enums/OfferChangeType';
import { LocationKinds } from '@/common/enums/LocationKinds';
import { deepClone } from '@/_utils/deep-clone';
import { HttpParams } from '@angular/common/http';

interface DialogData extends Offer {
	type: string;
	reservationStatus: Status;
}

export enum PriceSettingServicesNames {
	ADDITIONAL_ROOM_SIZE_CHARGE = 'Dopłata do wolnego miejsca',
	SURCHARGE_PROGRAM = 'Opłata programowa',
	OWN_TRANSPORT = 'Dojazd własny',
}

@Component({
	selector: 'app-reservation-summary-accomodation-offer-rooms-dialog',
	templateUrl: './reservation-summary-accomodation-offer-rooms-dialog.component.html',
	styleUrls: ['./reservation-summary-accomodation-offer-rooms-dialog.component.scss'],
})
export class ReservationSummaryAccomodationOfferRoomsDialogComponent implements OnInit, OnDestroy {
	constructor(
		public dialog: MatDialog,
		public store: ReservationSummaryStoreService,
		public api: ReservationSummaryApiService,
		protected loader: LoaderService,
		private readonly date: DateService,
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly dateAdapter: DateAdapter<any>,
		public dialogRef: MatDialogRef<ReservationSummaryAccomodationOfferRoomsDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: DialogData,
	) {}
	displayedColumns: string[] = [
		'select',
		'term',
		'roomType',
		'city',
		'board',
		'currentPrice',
		'promotion',
		'price',
		'transport',
		'departure',
		'buttons',
	];
	dataSource: MatTableDataSource<ChangeOfferSelected[]> = new MatTableDataSource<ChangeOfferSelected[]>();
	offersSubject: Observable<ChangeOfferSelected[][]>;
	selection = new SelectionModel<any>(true, []);
	@ViewChild(MatPaginator) paginator: MatPaginator;
	filtersLoaded = false;
	surchargeService: OfferService;
	sortedOffers: OfferFull[][] = [];
	offersNumber = 0;
	offersLimit = 50;
	filters: ChangeOfferFilters = {
		boards: [this.data.board?.id],
		slots: this.data.slot?.length,
		roomTypes: [this.data.roomType?.id],
		products: [this.data.product?.id],
		conveniences: this.data.product?.conveniences,
		locations: this.data.product?.location?.map((v) => this.findLocation(v, LocationKinds.COUNTRY)),
		stops: this.data.slot?.map((v) => v?.stopDeparture?.stop.id).filter((v) => v != undefined),
		productKinds: [this.data.product?.productKind?.id],
		promotion: this.data.promotion?.map((v) => v.catalog.id),
		dateRange: {
			lower: this.data.term?.dateRange.lower,
			upper: this.data.term?.dateRange.upper,
		},
		nightsMin: this.data.term?.nightsStay,
		nightsMax: this.data.term?.nightsStay,
	};
	ngOnInit() {
		this.loader.show();
		const { offers, offerService } = this.store.getters();
		const { getOfferFull, getOfferService } = this.api.actions();
		this.getFilters();
		getOfferFull(this.buildFilterParams(), this.offersLimit).then(() => {
			this.loader.hide();
		});
		if (!this.data.oskarFault && this.data.term) {
			const params = new HttpParams()
				.set('date_range', `${this.data?.term.dateRange.lower},${this.data?.term.dateRange.upper}`)
				.set('products', String(this.data?.product?.id))
				.set('kinds', String(this.data?.product?.productKind.id))
				.set('limit', '999');

			const queryString = `?${params.toString()}`;

			getOfferService(queryString);
			offerService().subscribe((services) => {
				this.surchargeService = this.findSurchargeService(services);
			});
		}
		offers().subscribe((value) => {
			if (value) {
				this.offersNumber = value.length;
				this.dataSource.data = this.sortOffersByProduct(value);
			}
		});
		this.setPaginatorData();
		this.dateAdapter.setLocale('pl');
	}

	private findSurchargeService(services: OfferService[]): OfferService | null {
		return services.find((v) => v.id === this.data.offerChangeService) ?? null;
	}

	get offer() {
		return this.sortedOffers.map((v) => {
			return v.map((el): ChangeOfferSelected => {
				const surcharge = el.services.find(
					(service) => service.optionName === PriceSettingServicesNames.SURCHARGE_PROGRAM,
				);
				return {
					board: {
						id: el.board.id,
						name: el.board.name,
						price: el.board.price,
						additionalRoomSizeCharge:
							el.services.find(
								(service) =>
									service.optionName === PriceSettingServicesNames.ADDITIONAL_ROOM_SIZE_CHARGE,
							)?.value ?? 0,
					},
					term: el.term.id,
					promotion: el.promotion,
					promotionValue: el.promotion ? el.catalogPrice - el.fullPrice : 0,
					promotionCatalog: el?.promotion?.catalog?.id,
					dateRange: el.dateRange,
					fee: el.stop.fee,
					fullPrice: el.fullPrice - el.board.price,
					catalogPrice: el.catalogPrice - el.stop.fee - el.board.price,
					priceDiff: el.fullPrice - this.currentOfferSlotFullPrice,
					nightsStay: el.term.nightStay,
					product: {
						id: el.product.id,
						name: el.product.name,
						productKind: el.product.kind,
						locations: el.locations,
						insurer: el.product.insurer,
						insurance: el.product.insurance,
					},
					participants: {
						adults: el.roomType?.id ? el.roomType.adults : this.filters.slots,
						children: el.roomType?.id ? el.roomType.children : 0,
					},
					roomType: {
						id: el.roomType?.id,
						name: el.roomType?.name,
					},
					transportKind: el.transport.kind,
					stop: {
						id: el.stop.id,
						name: el.stop.name,
						transportDeparture: el.transport.transportDeparture,
						transportReturn: el.transport.transportReturn,
					},
					wasTransportAvailable: !!el.services.find(
						(service) => service.optionName === PriceSettingServicesNames.OWN_TRANSPORT,
					),
					surchargeFee: surcharge?.value ?? 0,
					surchargeCurrency: surcharge?.currency ?? null,
					surchargeForExtraRoomSpace: 0,
					insurer: null,
					insurance: null,
				};
			});
		});
	}

	private findLocation(location: Location, locationKind: string) {
		if (location.locationKind === locationKind) return location.id;
		if (!location.parentLocation) return;
		return this.findLocation(location.parentLocation, locationKind);
	}

	get currentOfferSlotFullPrice(): number {
		const adultSlot = this.data.slot.find(
			(v) => v.kind === 'A' && !CancellationStatuses.includes(v.status as Status),
		);
		return adultSlot ? adultSlot.catalogPrice + adultSlot.priceBoard - adultSlot.promotionValue : 0;
	}

	private getFilters() {
		const {
			getBoards,
			getRoomTypes,
			getProducts,
			getConveniences,
			getLocations,
			getStops,
			getProductKinds,
			getPromotionsCatalog,
		} = this.api.actions();
		if (
			!this.store.locations.length &&
			!this.store.products.length &&
			!this.store.boards.length &&
			!this.store.conveniences.length &&
			!this.store.stops.length &&
			!this.store.productKinds.length &&
			!this.store.promotions.length &&
			!this.store.roomTypes.length
		) {
			const requests = [
				getConveniences(),
				getProducts(),
				getBoards(),
				getRoomTypes(),
				getLocations(),
				getStops(),
				getProductKinds(),
				getPromotionsCatalog(),
			];
			Promise.allSettled(requests).then(() => {
				this.filtersLoaded = true;
			});
		} else this.filtersLoaded = true;
	}
	sortOffersByProduct(value: OfferFull[]) {
		this.sortedOffers = [];
		value.forEach((item) => {
			if (!this.sortedOffers.length) return (this.sortedOffers[0] = [item]);
			let addedOffers: OfferFull[] = [];
			this.sortedOffers.forEach((v) => {
				v.forEach((el) => {
					addedOffers = [...addedOffers, el];
				});
			});
			if (
				addedOffers.find((v) => {
					return v.product.id === item.product.id;
				})
			) {
				const i = this.sortedOffers.findIndex((v) => {
					return v[0]?.product.id === item.product.id;
				});
				this.sortedOffers[i] = [...this.sortedOffers[i], item];
				return;
			}
			this.sortedOffers[this.sortedOffers.length] = [item];
		});
		return this.offer;
	}

	private setPaginatorData() {
		this.changeDetectorRef.detectChanges();
		this.translateMatPaginator(this.paginator);
		this.dataSource.paginator = this.paginator;
		this.offersSubject = this.dataSource.connect();
	}

	private translateMatPaginator(paginator: MatPaginator) {
		paginator._intl.firstPageLabel = 'Pierwsza strona';
		paginator._intl.itemsPerPageLabel = 'Produkty na stronę';
		paginator._intl.lastPageLabel = 'Ostatnia strona';
		paginator._intl.nextPageLabel = 'Następna strona';
		paginator._intl.previousPageLabel = 'Poprzednia strona';
	}

	private buildFilterParams() {
		const urlBuilder = new OskarOfferUrlBuilder();
		const lower = this.convertDateRange(this.filters.dateRange.lower);
		const upper = this.convertDateRange(this.filters.dateRange.upper);
		urlBuilder
			.setAdults(this.filters.slots ? this.filters.slots.toString() : '')
			.setBoards(this.filters.boards)
			.setRoomType(this.filters.roomTypes)
			.setProduct(this.filters.products)
			.setConveniences(this.filters.conveniences)
			.setLocations(this.filters.locations)
			.setStops(this.filters.stops)
			.setKinds(this.filters.productKinds)
			.setPromotion(this.filters.promotion);

		if (lower && upper) {
			urlBuilder.setDateRange(lower, upper);
		}

		urlBuilder.setMaxNights(this.filters.nightsMax ?? 15).setMinNights(this.filters.nightsMin ?? 0);

		return urlBuilder.build();
	}

	/** Whether the number of selected elements matches the total number of rows. */
	isAllSelected(i: number) {
		const numSelected = this.selection.selected.filter(
			(v) => v?.product.id === this.dataSource.data[i][0].product.id,
		).length;
		const numRows = this.dataSource.data[i].length;
		return numSelected === numRows;
	}

	checkboxLabel(row?: any): string {
		if (!row) {
			return `${this.isAllSelected(0) ? 'deselect' : 'select'} all`;
		}
		return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
	}

	/** Selects all rows if they are not all selected; otherwise clear selection. */
	toggleAllRows(i: number) {
		if (this.isAllSelected(i)) {
			this.selection.deselect(...this.dataSource.data[i]);
			return;
		}
		this.selection.select(...this.dataSource.data[i]);
	}

	clearDate(event) {
		event.stopPropagation();
		this.filters.dateRange.lower = null;
		this.filters.dateRange.upper = null;
		this.setFilters();
	}

	private convertDateRange(date: string): string {
		return date ? this.date.datetimeWithTimezone(new Date(date)).date.toString() : '';
	}

	setFilters() {
		this.loader.show();
		const { getOfferFull } = this.api.actions();
		this.sortedOffers = [];
		getOfferFull(this.buildFilterParams(), this.offersLimit).then(() => {
			this.loader.hide();
		});
	}

	openChangeOfferDialog(selectedOffer: ChangeOfferSelected) {
		this.dialog.open(ReservationSummaryAccomodationOfferRoomsDialogOfferChangeDialogComponent, {
			width: '1000px',
			disableClose: true,
			data: {
				currentOffer: deepClone(this.data),
				selectedOffer,
				surchargeService: this.surchargeService,
				reservationStatus: this.data.reservationStatus,
			},
		});
	}

	closeDialog() {
		this.dialogRef.close();
	}

	ngOnDestroy() {
		if (this.dataSource) this.dataSource.disconnect();
	}

	protected readonly Status = Status;
	protected readonly OfferChangeTypes = OfferChangeTypes;
}
